/**
 * The driver's jobs: what's coming up (given to them in advance, or taken from the job
 * board), the job board itself if the firm uses it, and what they've done.
 */
import { ApiError, errorMessage, get, post } from "@taxi/mobile-web/api";
import { miles, money, when } from "@taxi/mobile-web/format";
import { useResource } from "@taxi/mobile-web/hooks";
import { ListIcon, UsersIcon } from "@taxi/mobile-web/icons";
import { Link, navigate, useLocation } from "@taxi/mobile-web/router";
import {
  Button,
  Card,
  Empty,
  Loading,
  Notice,
  Segmented,
  TopBar,
  useToast,
} from "@taxi/mobile-web/ui";
import { useCallback, useEffect, useState } from "react";
import { PaymentTag, pickupWhen, TripRoute } from "../components/parts";
import { useDriver } from "../lib/driver";
import { useSession } from "../lib/session";
import type { BoardJob, DriverJob, HistoryItem, Page } from "../lib/types";

type Tab = "upcoming" | "board" | "done";

export function JobsScreen() {
  const { firm } = useSession();
  const { query } = useLocation();
  const tabs: { value: Tab; label: string }[] = [
    { value: "upcoming", label: "Upcoming" },
    ...(firm.jobBoardEnabled ? [{ value: "board" as const, label: "Job board" }] : []),
    { value: "done", label: "Done" },
  ];
  const asked = query.get("show");
  const tab = tabs.find((t) => t.value === asked)?.value ?? "upcoming";
  return (
    <div className="jobs-screen">
      <TopBar title="Jobs" />
      <div className="screen-body">
        <Segmented
          label="Show"
          value={tab}
          options={tabs}
          onChange={(value) =>
            navigate(value === "upcoming" ? "/jobs" : `/jobs?show=${value}`, { replace: true })
          }
        />
        {tab === "upcoming" && <Upcoming />}
        {tab === "board" && <JobBoard />}
        {tab === "done" && <Done />}
      </div>
    </div>
  );
}

function JobCard({ job, label }: { job: DriverJob; label?: string }) {
  return (
    <Link to={`/jobs/${job.id}`} className="job-card">
      <div className="job-card-head">
        <strong>{label ?? pickupWhen(job)}</strong>
        <span className="job-card-fare">{money(job.finalFarePence ?? job.pricePence)}</span>
      </div>
      <TripRoute pickup={job.pickup} stops={job.stops} dropoff={job.dropoff} />
      <div className="job-card-foot">
        <PaymentTag method={job.paymentMethod} />
        <span>
          <UsersIcon size={14} /> {job.passengers}
        </span>
        <span>Job {job.number}</span>
      </div>
    </Link>
  );
}

function Upcoming() {
  const { jobs } = useDriver();
  if (!jobs) return <Loading />;
  if (!jobs.current && jobs.queue.length === 0) {
    return (
      <Empty icon={<ListIcon />} title="Nothing coming up">
        <p>Jobs the office gives you in advance, or that you take from the job board, show here.</p>
      </Empty>
    );
  }
  return (
    <div className="stack">
      {jobs.current && <JobCard job={jobs.current} label="Now: your current job" />}
      {jobs.queue.map((job) => (
        <JobCard key={job.id} job={job} />
      ))}
      {jobs.queue.length > 0 && (
        <p className="fine-print">These are yours: each is offered to you first when it's due.</p>
      )}
    </div>
  );
}

function JobBoard() {
  const { boardVersion, refresh, me } = useDriver();
  const board = useResource(() => get<{ data: BoardJob[] }>("/driver/job-board"), [boardVersion]);
  const toast = useToast();
  const [taking, setTaking] = useState<string | null>(null);

  async function take(job: BoardJob) {
    setTaking(job.id);
    try {
      const taken = await post<DriverJob>(`/driver/job-board/${job.id}/take`);
      void refresh();
      if (taken.status === "accepted") navigate(`/jobs/${taken.id}`);
      else toast(`It's yours: ${pickupWhen(taken)}. It's in Upcoming.`);
    } catch (error) {
      toast(
        error instanceof ApiError && error.status === 409
          ? "Someone else has just taken it"
          : errorMessage(error),
        "error",
      );
    } finally {
      setTaking(null);
      void board.reload();
    }
  }

  if (board.error) return <Notice tone="error">{errorMessage(board.error)}</Notice>;
  if (!board.data) return <Loading />;
  const jobs = board.data.data;
  return (
    <div className="stack">
      {me.live.status === "off" && (
        <Notice tone="info">Clock on to take jobs from the board.</Notice>
      )}
      {jobs.length === 0 ? (
        <Empty icon={<ListIcon />} title="Nothing on the board">
          <p>Jobs without a driver show up here as they come in. First come, first served.</p>
        </Empty>
      ) : (
        jobs.map((job) => (
          <Card key={job.id} className="board-job">
            <div className="job-card-head">
              <strong>{pickupWhen(job)}</strong>
              <span className="job-card-fare">
                {money(job.pricePence)}
                {job.isEstimate && <small> est.</small>}
              </span>
            </div>
            <TripRoute pickup={job.pickup} dropoff={job.dropoff} />
            <div className="job-card-foot">
              <PaymentTag method={job.paymentMethod} />
              <span>
                <UsersIcon size={14} /> {job.passengers}
              </span>
              <span>{job.vehicleTypeName}</span>
              {job.distanceMetres !== null && <span>{miles(job.distanceMetres)}</span>}
            </div>
            <Button
              variant="secondary"
              loading={taking === job.id}
              disabled={taking !== null || me.live.status === "off"}
              onClick={() => take(job)}
            >
              Take this job
            </Button>
          </Card>
        ))
      )}
    </div>
  );
}

function Done() {
  const [items, setItems] = useState<HistoryItem[]>([]);
  const [next, setNext] = useState<string | null | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (cursor?: string) => {
    setLoading(true);
    setError(null);
    try {
      const page = await get<Page<HistoryItem>>(
        `/driver/history?limit=20${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}`,
      );
      setItems((current) => (cursor ? [...current, ...page.data] : page.data));
      setNext(page.nextCursor);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error && items.length === 0) return <Notice tone="error">{error}</Notice>;
  if (next === undefined) return <Loading />;
  if (items.length === 0) {
    return (
      <Empty icon={<ListIcon />} title="No jobs yet">
        <p>The jobs you finish show up here.</p>
      </Empty>
    );
  }
  return (
    <div className="stack">
      <ul className="history">
        {items.map((item) => (
          <li key={item.id}>
            <Link to={`/jobs/${item.id}`} className="history-item">
              <span className="history-when">
                {when(new Date(item.completedAt ?? item.pickupAt))}
              </span>
              <span className="history-route">
                {item.pickup.name || item.pickup.address.split(",")[0]} →{" "}
                {item.dropoff.name || item.dropoff.address.split(",")[0]}
              </span>
              <span className="history-money">
                {item.status === "no_show" ? (
                  "No-show"
                ) : (
                  <>
                    {money(item.farePence)}
                    {item.tipPence > 0 && <small> + {money(item.tipPence)} tip</small>}
                  </>
                )}
              </span>
            </Link>
          </li>
        ))}
      </ul>
      {error && <Notice tone="error">{error}</Notice>}
      {next && (
        <Button variant="secondary" loading={loading} onClick={() => load(next)}>
          Show more
        </Button>
      )}
    </div>
  );
}
